import jsPDF from "jspdf";

export default function generateFreeMealPdf(
  { config = {}, receipt = {} },
  { filename = "freemeal.pdf", paperWidth = 72, lineHeight = 4.2 } = {}
) {
  const textSize = {
    small: 8,
    normal: 9,
    big: 11,
    title: 12,
  };

  // Rough height estimate so we don’t cut off long lists (will be generous)
  const estLines = 16 + (receipt.items?.length || 0) * 3 + 24;
  const basePadding = 24;
  const height = basePadding + estLines * lineHeight;

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: [paperWidth, height],
  });

  const pageInnerWidth = paperWidth - 8; // left/right 4mm
  const leftX = 4;
  const rightX = paperWidth - 4;
  let y = 8;

  const setFont = (size = "normal", bold = false) => {
    doc.setFontSize(textSize[size] ?? textSize.normal);
    doc.setFont("helvetica", bold ? "bold" : "normal");
  };

  const centerText = (text, size = "normal", bold = false) => {
    setFont(size, bold);
    doc.text(String(text ?? ""), paperWidth / 2, y, { align: "center" });
    y += lineHeight;
  };

  const leftText = (text, size = "normal", bold = false) => {
    setFont(size, bold);
    doc.text(String(text ?? ""), leftX, y, { align: "left" });
    y += lineHeight;
  };

  const rightText = (text, size = "normal", bold = false) => {
    setFont(size, bold);
    doc.text(String(text ?? ""), rightX, y, { align: "right" });
    y += lineHeight;
  };

  const wrapCenter = (text, size = "normal", bold = false) => {
    setFont(size, bold);
    const lines = doc.splitTextToSize(String(text ?? ""), pageInnerWidth);
    lines.forEach((ln) => {
      doc.text(ln, paperWidth / 2, y, { align: "center" });
      y += lineHeight;
    });
  };

  const wrapLeft = (text, size = "normal", bold = false, indent = 0) => {
    setFont(size, bold);
    const lines = doc.splitTextToSize(
      String(text ?? ""),
      pageInnerWidth - indent
    );
    lines.forEach((ln) => {
      doc.text(ln, leftX + indent, y, { align: "left" });
      y += lineHeight;
    });
  };

  const rule = (chars = "=============================") => {
    centerText(chars, "normal", false);
  };

  // Machine selection (SN/MIN)
  const useAltMachine =
    receipt?.machineType === "cash" || receipt?.paymentType === "Cash";
  const SN = useAltMachine ? config?.SN2 || config?.SN : config?.SN;
  const MIN = useAltMachine ? config?.MIN2 || config?.MIN : config?.MIN;

  // HEADER
  setFont("normal", false);
  centerText(config?.companyName ?? "", "normal", false);
  wrapCenter(config?.address ?? "", "normal", false);
  centerText(`VAT Registered TIN: ${config?.TIN ?? ""}`, "normal", false);
  centerText(`SN: ${SN ?? ""} MIN: ${MIN ?? ""}`, "normal", false);

  y -= lineHeight * 0.4; // tighten spacing
  rule();
  y -= lineHeight * 0.2;

  centerText("Acknowledgement Receipt", "title", false);
  y -= lineHeight * 0.2;

  centerText(`Order No.: ${receipt?.orderNumber ?? ""}`, "normal", false);
  centerText(
    `Date/Time: ${new Date(receipt?.createdAt || Date.now()).toLocaleString(
      "en-PH",
      { timeZone: "Asia/Manila" }
    )}`,
    "normal",
    false
  );

  y -= lineHeight * 0.2;
  rule();

  centerText("----------  FREE MEALS  ----------", "big", false);
  y -= lineHeight * 0.3;

  // ITEMS (all zero value)
  setFont("normal", false);
  (receipt?.items || []).forEach((it) => {
    const lineLeft = `${it?.amount ?? 1} x ${it?.name ?? ""}`;
    // left + right amount line
    setFont("normal", false);
    doc.text(lineLeft, leftX + 2, y, { align: "left" });
    doc.text("0.00", rightX, y, { align: "right" });
    y += lineHeight;

    // options
    (it?.options || []).forEach((opt) => {
      const choices = Array.isArray(opt?.choices) ? opt.choices.join(", ") : "";
      wrapLeft(`- ${opt?.name ?? ""}: ${choices}`, "small", false, 6);
    });
  });

  centerText("-----------------------------", "normal", false);
  rightText("Subtotal:      0.00", "normal", false);
  rightText("Discount (Voucher):      0.00", "normal", false);
  rightText("Total:      0.00", "normal", true);
  rule();

  // PAYMENT / TENDER (all zeros)
  centerText("Payment Option: Complimentary", "normal", false);
  rightText("Tendered:      0.00", "normal", false);
  rightText("Change Due:      0.00", "normal", false);
  rule();

  // VAT / BREAKDOWN (all zeros)
  rightText("Vatable Sales:      0.00", "normal", false);
  rightText("VAT Amount:      0.00", "normal", false);
  rightText("VAT Exempt Sales:      0.00", "normal", false);
  rightText("Zero-rated Sales:      0.00", "normal", false);
  rule();

  // Signature + Employee Info
  y += lineHeight * 0.4;
  leftText("Sign: __________________________", "normal", false);
  y += lineHeight * 0.2;

  centerText("Employee Information", "normal", true);
  y += lineHeight * 0.2;

  leftText(
    `Name: ${receipt?.employeeName || receipt?.cashierName || ""}`,
    "normal",
    false
  );
  leftText("Address: _____________________", "normal", false);
  leftText("TIN: __________________________", "normal", false);
  leftText("Business Style: ____________________", "normal", false);

  // FOOTER
  y += lineHeight * 0.2;
  centerText("THIS IS NOT A SALES INVOICE", "normal", false);
  centerText("** FREE MEAL **", "normal", false);
  y += lineHeight * 0.3;
  centerText("POS PROVIDER: SparkleStar International Corp.", "small", false);
  wrapCenter(config?.address ?? "", "small", false);

  // Output
  const pdfBlob = doc.output("blob");
  const url = URL.createObjectURL(pdfBlob);

  // Also save a file immediately
  doc.save(filename);

  return { pdfBlob, url };
}
